import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ItemCard } from '../types/itemCard';
import { ItemCardService } from '../services/itemCardService';
import { CoinManagementService } from '../services/coinManagementService';
import { userDataService } from '../services/userDataService';
import { ClassMembersPartitionService } from '../services/classMembersPartitionService';
import { defaultSetService } from '../services/defaultSetService';
import LicenseLimitModal from './LicenseLimitModal';
import { withImageVersion } from '../utils/imageVersion';

const PROFILE_SETS = {
  B: Array.from({ length: 21 }, (_, i) => `b-${i}`),
  C: Array.from({ length: 21 }, (_, i) => `c-${i}`)
};

const DEFAULT_PROFILE_CARD_COST = 100;

interface StoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  classId: string;
  studentId: string;
  memberGroup: string;
  currentCoins: number;
  userType?: string;
  onPurchaseSuccess?: () => void;
}

const StoreModal: React.FC<StoreModalProps> = ({
  isOpen,
  onClose,
  userId,
  classId,
  studentId,
  memberGroup,
  currentCoins,
  userType,
  onPurchaseSuccess
}) => {
  const normalizedUserType = userType || 'standard';

  const [storeItems, setStoreItems] = useState<ItemCard[]>([]);
  const [selectedStoreItem, setSelectedStoreItem] = useState<ItemCard | null>(null);
  const [isLoadingStore, setIsLoadingStore] = useState<boolean>(false);
  const [isPurchasing, setIsPurchasing] = useState<boolean>(false);
  const [storeTab, setStoreTab] = useState<'itemCard' | 'profileCard'>(() =>
    normalizedUserType === 'basic' ? 'profileCard' : 'itemCard'
  );
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');

  const [isPurchasingProfile, setIsPurchasingProfile] = useState<boolean>(false);
  const [profileDrawResult, setProfileDrawResult] = useState<{ show: boolean; profileId: string; isNew: boolean } | null>(null);
  const [profileCardCostB, setProfileCardCostB] = useState<number>(DEFAULT_PROFILE_CARD_COST);
  const [profileCardCostC, setProfileCardCostC] = useState<number>(DEFAULT_PROFILE_CARD_COST);
  const [isProfileRevealed, setIsProfileRevealed] = useState(false);
  const [selectedProfileSet, setSelectedProfileSet] = useState<'B' | 'C' | null>(null);

  const [purchaseToast, setPurchaseToast] = useState<{ show: boolean; cost: number; itemName: string }>({
    show: false,
    cost: 0,
    itemName: ''
  });
  const purchaseToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [insufficientToast, setInsufficientToast] = useState<boolean>(false);
  const [localCoins, setLocalCoins] = useState<number>(currentCoins);

  useEffect(() => {
    if (isOpen) {
      setLocalCoins(currentCoins);
    }
  }, [currentCoins, isOpen]);

  useEffect(() => {
    if (!isOpen || !userId || !classId) return;
    setIsLoadingStore(true);
    setSelectedStoreItem(null);

    const loadStoreData = async () => {
      try {
        const itemCardService = new ItemCardService(userId, classId);
        const itemCardSet = await itemCardService.loadItemCardSet();
        setStoreItems(itemCardSet?.storeItem || []);

        const defaultSet = await defaultSetService.loadDefaultSet(userId, classId);
        if (defaultSet?.profileCardCostB !== undefined) {
          setProfileCardCostB(defaultSet.profileCardCostB);
        }
        if (defaultSet?.profileCardCostC !== undefined) {
          setProfileCardCostC(defaultSet.profileCardCostC);
        }
      } catch (error) {
        console.error('스토어 아이템 로드 실패:', error);
        setStoreItems([]);
      } finally {
        setIsLoadingStore(false);
      }
    };

    loadStoreData();
  }, [isOpen, userId, classId]);

  const handlePurchaseStoreItem = async () => {
    if (!userId || !classId || !selectedStoreItem || !studentId) return;

    if (normalizedUserType === 'free' || normalizedUserType === 'basic') {
      setLicenseLimitMessage('프리/베이직 플랜에서는 스토어를 사용할 수 없습니다. 이용권을 업그레이드하세요.');
      setShowLicenseLimitModal(true);
      return;
    }

    const itemCost = typeof selectedStoreItem.cost === 'number' ? selectedStoreItem.cost : 0;
    if (localCoins < itemCost) {
      setInsufficientToast(true);
      setTimeout(() => setInsufficientToast(false), 2500);
      return;
    }

    setIsPurchasing(true);

    try {
      const purchaseMemo = `${selectedStoreItem.name} 스토어 카드 구입`;
      const coinManagementService = new CoinManagementService(userId, classId, memberGroup);
      await coinManagementService.grantCoinsToStudent(studentId, -itemCost, purchaseMemo, 'Store');

      const instanceId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newItemCard: any = {
        instanceId,
        itemCardId: selectedStoreItem.itemCardId,
        type: selectedStoreItem.type,
        acquiredAt: new Date(),
        category: selectedStoreItem.category || 'store'
      };
      if (selectedStoreItem.type === 'active' && selectedStoreItem.useCount !== undefined) {
        newItemCard.remainingUseCount = selectedStoreItem.useCount;
      }
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      await partitionService.addStudentItemCardAtomic(studentId, newItemCard);

      const updatedCoinData = await coinManagementService.getStudentCoinData(studentId);
      if (updatedCoinData) {
        setLocalCoins(updatedCoinData.coins || 0);
      }

      if (purchaseToastTimeoutRef.current) clearTimeout(purchaseToastTimeoutRef.current);
      setPurchaseToast({
        show: true,
        cost: itemCost,
        itemName: selectedStoreItem.name
      });

      // 토스트를 충분히 보여준 뒤 콜백만 호출 (스토어·학생 상세 모달 유지, 데이터만 갱신)
      purchaseToastTimeoutRef.current = setTimeout(() => {
        purchaseToastTimeoutRef.current = null;
        setPurchaseToast({ show: false, cost: 0, itemName: '' });
        onPurchaseSuccess?.();
        setSelectedStoreItem(null);
      }, 2000);
    } catch (error: any) {
      console.error('스토어 아이템 구매 실패:', error);
      alert(`구매에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handlePurchaseProfileCard = async (targetSet: 'B' | 'C') => {
    if (!userId || !classId || !studentId) return;

    if (normalizedUserType === 'free' || normalizedUserType === 'basic') {
      setLicenseLimitMessage('프리/베이직 플랜에서는 스토어를 사용할 수 없습니다. 이용권을 업그레이드하세요.');
      setShowLicenseLimitModal(true);
      return;
    }

    const cost = targetSet === 'B' ? profileCardCostB : profileCardCostC;
    if (localCoins < cost) {
      setInsufficientToast(true);
      setTimeout(() => setInsufficientToast(false), 2500);
      return;
    }

    setIsPurchasingProfile(true);
    try {
      const classMembers = await userDataService.loadClassMembers(userId, classId, memberGroup);
      const targetMemberIndex = classMembers.findIndex(member => member.id === studentId);
      if (targetMemberIndex === -1) throw new Error('멤버를 찾을 수 없습니다.');

      const currentMember = classMembers[targetMemberIndex];
      const unlockedProfiles = currentMember.unlockedProfiles || [];
      const setProfiles = PROFILE_SETS[targetSet];
      const availableProfiles = setProfiles.filter(p => !unlockedProfiles.includes(p));

      if (availableProfiles.length === 0) {
        alert(`세트 ${targetSet}의 모든 프로필이 이미 잠금 해제되었습니다!`);
        setIsPurchasingProfile(false);
        return;
      }

      const randomIndex = Math.floor(Math.random() * availableProfiles.length);
      const selectedProfile = availableProfiles[randomIndex];
      const isNew = !unlockedProfiles.includes(selectedProfile);

      const purchaseMemo = `프로필 카드 구입 - 세트 ${targetSet} (${selectedProfile})`;
      const coinManagementService = new CoinManagementService(userId, classId, memberGroup);
      await coinManagementService.grantCoinsToStudent(studentId, -cost, purchaseMemo, 'Store');

      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      await partitionService.addStudentUnlockedProfileAtomic(studentId, selectedProfile);

      const updatedCoinData = await coinManagementService.getStudentCoinData(studentId);
      if (updatedCoinData) {
        setLocalCoins(updatedCoinData.coins || 0);
      }

      setIsProfileRevealed(false);
      setProfileDrawResult({ show: true, profileId: selectedProfile, isNew });
      setTimeout(() => setIsProfileRevealed(true), 3000);
    } catch (error: any) {
      console.error('프로필 카드 구매 실패:', error);
      alert(`구매에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      setIsPurchasingProfile(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {!profileDrawResult?.show && (
        <StoreModalOverlay
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
      )}
      <StoreModalContainer
        $isFreePlan={normalizedUserType === 'free'}
        onClick={(e) => e.stopPropagation()}
      >
        <StoreModalHeader>
          <StoreModalTitle>스토어</StoreModalTitle>
          <StoreModalCloseButton onClick={onClose}>×</StoreModalCloseButton>
        </StoreModalHeader>

        <StoreTabContainer>
          <StoreTabButton
            $isActive={storeTab === 'itemCard'}
            $isDisabled={normalizedUserType === 'free' || normalizedUserType === 'basic'}
            onClick={() => {
              if (normalizedUserType === 'free' || normalizedUserType === 'basic') {
                setLicenseLimitMessage('프리/베이직 플랜에서는 아이템 카드를 구입할 수 없습니다. 이용권을 업그레이드하세요.');
                setShowLicenseLimitModal(true);
                return;
              }
              setStoreTab('itemCard');
            }}
          >
            아이템 카드 구입
          </StoreTabButton>
          <StoreTabButton
            $isActive={storeTab === 'profileCard'}
            $isDisabled={normalizedUserType === 'free'}
            onClick={() => {
              if (normalizedUserType === 'free') {
                setLicenseLimitMessage('프리 플랜에서는 프로필 카드를 구입할 수 없습니다. 이용권을 업그레이드하세요.');
                setShowLicenseLimitModal(true);
                return;
              }
              setStoreTab('profileCard');
            }}
          >
            프로필 카드 구입
          </StoreTabButton>
        </StoreTabContainer>

        <StoreModalBody>
          {normalizedUserType === 'free' && (
            <FreePlanNotice>
              프리 플랜에서는 스토어 기능을 사용할 수 없습니다.<br/>
              아이템 카드와 프로필 카드 기능을 이용하려면 이용권을 업그레이드하세요.
            </FreePlanNotice>
          )}
          {storeTab === 'itemCard' ? (
            <>
              {isLoadingStore ? (
                <StoreLoadingMessage>스토어 아이템을 불러오는 중...</StoreLoadingMessage>
              ) : (
                <>
                  <StoreItemGrid>
                    {storeItems.length === 0 ? (
                      <StoreItemCard $isSelected={false} style={{ opacity: 0.5, cursor: 'default' }}>
                        <StoreItemCardImage>
                          <StoreItemCardPlaceholder>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          </StoreItemCardPlaceholder>
                        </StoreItemCardImage>
                        <StoreItemName style={{ color: 'var(--color-text-muted)' }}>아이템 없음</StoreItemName>
                        <StoreItemMemo style={{ color: 'var(--color-text-muted)', fontSize: '12px', marginTop: '8px' }}>
                          아이템 카드 설정에서 스토어 아이템을 등록해주세요.
                        </StoreItemMemo>
                      </StoreItemCard>
                    ) : (
                      storeItems.map((item) => (
                        <StoreItemCard
                          key={item.itemCardId}
                          $isSelected={selectedStoreItem?.itemCardId === item.itemCardId}
                          onClick={() => setSelectedStoreItem(
                            selectedStoreItem?.itemCardId === item.itemCardId ? null : item
                          )}
                        >
                          <StoreItemCardImage>
                            {item.cardImage === 'default' ? (
                              <StoreItemCardPlaceholder>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                  <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                  <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                </svg>
                              </StoreItemCardPlaceholder>
                            ) : item.cardImage && /^https?:\/\//.test(item.cardImage) ? (
                              <img src={withImageVersion(item.cardImage, item.updatedAt)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : item.cardImage && /^\d+$/.test(item.cardImage) ? (
                              <img src={`/CardIcon/IconBasic/IconBasic-${item.cardImage}.png`} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : item.cardImage ? (
                              <StoreCardImageNumber>{item.cardImage}</StoreCardImageNumber>
                            ) : item.icon ? (
                              <img src={withImageVersion(item.icon, item.updatedAt)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <StoreItemCardPlaceholder>?</StoreItemCardPlaceholder>
                            )}
                          </StoreItemCardImage>
                          <StoreItemName>{item.name}</StoreItemName>
                          {item.memo && <StoreItemMemo>{item.memo}</StoreItemMemo>}
                          <StoreItemMetaGroup>
                            <StoreItemCost>
                              <StoreCoinIcon>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                  <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                  <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                </svg>
                              </StoreCoinIcon>
                              <span>×{typeof item.cost === 'number' ? item.cost : 0}</span>
                            </StoreItemCost>
                            {item.type === 'active' && (
                              <StoreItemUseCount>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                </svg>
                                <span>×{item.useCount || 1}</span>
                              </StoreItemUseCount>
                            )}
                          </StoreItemMetaGroup>
                        </StoreItemCard>
                      ))
                    )}
                  </StoreItemGrid>
                  {storeItems.length === 0 && (
                    <StoreEmptyDescription>
                      아이템 카드 설정 모달에서 스토어 아이템을 등록하면<br/>
                      여기에서 구매할 수 있습니다.
                    </StoreEmptyDescription>
                  )}
                </>
              )}
            </>
          ) : (
            <ProfileCardPurchaseContainer>
              <ProfileCardSetsRow>
                <ProfileCardSetItem
                  $isSelected={selectedProfileSet === 'B'}
                  onClick={() => setSelectedProfileSet('B')}
                >
                  <ProfileCardSetTitle>동물</ProfileCardSetTitle>
                  <ProfileCardStackWrapper>
                    <ProfileCardStack>
                      <ProfileCardBack $index={2} />
                      <ProfileCardBack $index={1} />
                      <ProfileCardFront>
                        <ProfileCardQuestion>?</ProfileCardQuestion>
                      </ProfileCardFront>
                    </ProfileCardStack>
                  </ProfileCardStackWrapper>
                  <ProfileCardCostRow>
                    <StoreCoinIcon>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                      </svg>
                    </StoreCoinIcon>
                    <ProfileCardCostText style={{ fontSize: '18px' }}>×{profileCardCostB}</ProfileCardCostText>
                  </ProfileCardCostRow>
                </ProfileCardSetItem>
                <ProfileCardSetItem
                  $isSelected={selectedProfileSet === 'C'}
                  onClick={() => setSelectedProfileSet('C')}
                >
                  <ProfileCardSetTitle>캐릭터</ProfileCardSetTitle>
                  <ProfileCardStackWrapper>
                    <ProfileCardStack>
                      <ProfileCardBack $index={2} />
                      <ProfileCardBack $index={1} />
                      <ProfileCardFront>
                        <ProfileCardQuestion>?</ProfileCardQuestion>
                      </ProfileCardFront>
                    </ProfileCardStack>
                  </ProfileCardStackWrapper>
                  <ProfileCardCostRow>
                    <StoreCoinIcon>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                      </svg>
                    </StoreCoinIcon>
                    <ProfileCardCostText style={{ fontSize: '18px' }}>×{profileCardCostC}</ProfileCardCostText>
                  </ProfileCardCostRow>
                </ProfileCardSetItem>
              </ProfileCardSetsRow>
              <ProfileCardDescription>
                세트를 선택하고 구매하기 버튼을 눌러주세요.<br/>
                해당 세트의 잠긴 프로필 중 하나를 랜덤으로 획득합니다.
              </ProfileCardDescription>
            </ProfileCardPurchaseContainer>
          )}
        </StoreModalBody>

        <StoreModalFooter>
          <StoreCurrentCoins>
            보유 코인:
            <StoreCoinIcon>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
              </svg>
            </StoreCoinIcon>
            <strong>{localCoins}</strong>
          </StoreCurrentCoins>
          {storeTab === 'itemCard' ? (
            <StorePurchaseButton
              disabled={!selectedStoreItem || isPurchasing || normalizedUserType === 'free' || normalizedUserType === 'basic'}
              onClick={handlePurchaseStoreItem}
            >
              {isPurchasing ? '구매 중...' : '구매하기'}
            </StorePurchaseButton>
          ) : (
            <StorePurchaseButton
              disabled={!selectedProfileSet || isPurchasingProfile || normalizedUserType === 'free'}
              onClick={() => {
                if (normalizedUserType === 'free') {
                  setLicenseLimitMessage('프리 플랜에서는 프로필 카드를 구입할 수 없습니다. 이용권을 업그레이드하세요.');
                  setShowLicenseLimitModal(true);
                  return;
                }
                selectedProfileSet && handlePurchaseProfileCard(selectedProfileSet);
              }}
            >
              {isPurchasingProfile ? '구매 중...' : '구매하기'}
            </StorePurchaseButton>
          )}
        </StoreModalFooter>
      </StoreModalContainer>

      {purchaseToast.show && selectedStoreItem && (
        <PurchaseToastOverlay onClick={(e) => e.stopPropagation()}>
          <PurchaseToastContent onClick={e => e.stopPropagation()}>
            <PurchaseToastImage>
              {selectedStoreItem.cardImage === 'default' ? (
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)" />
                  <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)" />
                  <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)" />
                </svg>
              ) : selectedStoreItem.cardImage && /^https?:\/\//.test(selectedStoreItem.cardImage) ? (
                <img src={selectedStoreItem.cardImage} alt={selectedStoreItem.name} />
              ) : selectedStoreItem.cardImage && /^\d+$/.test(selectedStoreItem.cardImage) ? (
                <img src={`/CardIcon/IconBasic/IconBasic-${selectedStoreItem.cardImage}.png`} alt={selectedStoreItem.name} />
              ) : selectedStoreItem.cardImage ? (
                <PurchaseToastImageNumber>{selectedStoreItem.cardImage}</PurchaseToastImageNumber>
              ) : selectedStoreItem.icon ? (
                <img src={selectedStoreItem.icon} alt={selectedStoreItem.name} />
              ) : (
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)" />
                  <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)" />
                  <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)" />
                </svg>
              )}
            </PurchaseToastImage>
            <PurchaseToastMessageWrap>
              <PurchaseToastMessage>{purchaseToast.itemName}</PurchaseToastMessage>
            </PurchaseToastMessageWrap>
            <PurchaseToastLabel>아이템 카드 구입</PurchaseToastLabel>
            <PurchaseToastCostRow>-{purchaseToast.cost} 코인</PurchaseToastCostRow>
            <PurchaseToastConfirmButton
              type="button"
              onClick={() => {
                if (purchaseToastTimeoutRef.current) {
                  clearTimeout(purchaseToastTimeoutRef.current);
                  purchaseToastTimeoutRef.current = null;
                }
                setPurchaseToast({ show: false, cost: 0, itemName: '' });
                onPurchaseSuccess?.();
                setSelectedStoreItem(null);
              }}
            >
              확인
            </PurchaseToastConfirmButton>
          </PurchaseToastContent>
        </PurchaseToastOverlay>
      )}

      {insufficientToast && (
        <InsufficientToast>코인이 부족합니다!</InsufficientToast>
      )}

      {profileDrawResult?.show && (
        <>
          <ProfileDrawResultOverlay onClick={(e) => e.stopPropagation()} />
          <ProfileDrawResultModal onClick={(e) => e.stopPropagation()}>
            <ProfileDrawTitle>프로필 카드 뽑기 결과</ProfileDrawTitle>
            <ProfileDrawImageContainer>
              {isProfileRevealed ? (
                <ProfileDrawResultImage>
                  <ProfileDrawResultText>{profileDrawResult.profileId}</ProfileDrawResultText>
                </ProfileDrawResultImage>
              ) : (
                <ProfileDrawQuestion>?</ProfileDrawQuestion>
              )}
            </ProfileDrawImageContainer>
            <ProfileDrawResultLabel>
              {isProfileRevealed
                ? (profileDrawResult.isNew ? '새로운 프로필 획득!' : '이미 보유한 프로필입니다')
                : '뽑는 중...'}
            </ProfileDrawResultLabel>
            <ProfileDrawCloseButton onClick={() => setProfileDrawResult(null)}>
              확인
            </ProfileDrawCloseButton>
          </ProfileDrawResultModal>
        </>
      )}

      {showLicenseLimitModal && (
        <LicenseLimitModal
          isOpen={showLicenseLimitModal}
          onClose={() => setShowLicenseLimitModal(false)}
          message={licenseLimitMessage}
        />
      )}
    </>
  );
};

export default StoreModal;

const StoreModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1100;
`;

const StoreModalContainer = styled.div<{ $isFreePlan?: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  z-index: 1101;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  ${props => props.$isFreePlan && `
    filter: grayscale(100%);
    pointer-events: none;
    user-select: none;
  `}
`;

const StoreModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const StoreModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const StoreModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const StoreModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const StoreTabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
`;

const StoreTabButton = styled.button<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 14px 16px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.$isDisabled ? 'transparent' :
                                props.$isActive ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$isDisabled ? 'var(--color-disabled-text)' :
                props.$isActive ? 'var(--color-accent-strong)' : '#6c757d'};
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  margin-bottom: -1px;

  &:hover {
    color: ${props => props.$isDisabled ? 'var(--color-disabled-text)' :
                  'var(--color-accent-text)'};
    background: ${props => props.$isDisabled ? 'transparent' : 'var(--color-accent-soft)'};
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }

  :root:not([data-theme='light']) & {
    color: ${props => props.$isActive ? '#ffffff' : '#6c757d'};
  }
`;

const StoreModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  background: var(--color-panel);
  border-radius: 0 0 16px 16px;
`;

const StoreCurrentCoins = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-text-secondary);

  strong {
    font-size: 16px;
    font-weight: 700;
    color: #f39c12;
  }
`;

const StorePurchaseButton = styled.button<{ disabled?: boolean }>`
  padding: 12px 28px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StoreLoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
`;

const FreePlanNotice = styled.div`
  text-align: center;
  padding: 20px;
  margin-bottom: 20px;
  background: var(--color-accent-soft);
  border: 1px solid var(--color-accent);
  border-radius: 8px;
  color: var(--color-accent-text);
  font-weight: 500;
  line-height: 1.5;
`;

const StoreEmptyDescription = styled.div`
  text-align: center;
  padding: 20px;
  margin-top: 20px;
  color: var(--color-text-secondary);
  font-size: 13px;
  line-height: 1.6;
  background: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const StoreItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 180px);
  gap: 20px;
  justify-content: center;
  padding: 10px 0;
`;

const StoreItemCard = styled.div<{ $isSelected: boolean }>`
  width: 180px;
  height: 300px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-strong)'};
  border-radius: 12px;
  background: ${props => props.$isSelected ? 'var(--color-accent-soft)' : 'var(--color-surface)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  :root:not([data-theme='light']) & {
    border: 2px solid ${props => props.$isSelected ? '#ffffff' : '#404040'};
  }

  &:hover {
    border-color: rgba(var(--color-accent-rgb), 0.6);
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  > * {
    flex-shrink: 0;
  }
`;

const StoreItemCardImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 12px;
  border: 2px solid var(--color-accent-strong);

  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }
`;

const StoreCardImageNumber = styled.span`
  font-size: 48px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const StoreItemCardPlaceholder = styled.span`
  font-size: 48px;
  color: #999;
  display: inline-flex;

  /* 다크모드: 스택 맨 뒤 카드를 흰색으로 (아이템 카드 설정 모달 기본 이미지와 동일) */
  :root:not([data-theme='light']) & svg rect:first-of-type {
    fill: #ffffff;
  }
`;

const StoreItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const StoreItemMemo = styled.div`
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 8px;
  line-height: 1.3;
  text-align: center;
  max-height: 30px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const StoreItemMetaGroup = styled.div`
  width: 100%;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
  padding-top: 8px;
`;

const StoreCoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StoreItemCost = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const StoreItemUseCount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

const ProfileCardPurchaseContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 24px;
`;

const ProfileCardSetsRow = styled.div`
  display: flex;
  gap: 40px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ProfileCardSetItem = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: var(--color-panel) !important;
  border-radius: 16px;
  transition: all 0.2s;
  cursor: pointer;

  border: 2px solid var(--color-border);

  ${props => props.$isSelected ? `
    border: 2px solid var(--color-accent-strong) !important;
    background: var(--color-panel) !important;
    box-shadow: 0 4px 16px rgba(var(--color-accent-rgb), 0.2) !important;
  ` : ''}

  &:hover {
    ${props => props.$isSelected ? `
      border: 2px solid var(--color-accent-strong) !important;
      box-shadow: 0 4px 16px rgba(var(--color-accent-rgb), 0.2) !important;
    ` : `
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 4px 16px rgba(var(--color-accent-rgb), 0.1);
  `}
  }

  :root:not([data-theme='light']) & {
    border: 2px solid ${props => props.$isSelected ? '#ffffff' : '#404040'} !important;
  }
  :root:not([data-theme='light']) &:hover {
    border: 2px solid ${props => props.$isSelected ? '#ffffff' : '#404040'} !important;
  }
`;

const ProfileCardSetTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-accent-text);
`;

const ProfileCardStackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ProfileCardStack = styled.div`
  position: relative;
  width: 140px;
  height: 180px;
`;

const ProfileCardBack = styled.div<{ $index: number }>`
  position: absolute;
  width: 120px;
  height: 160px;
  background: linear-gradient(135deg, var(--color-border) 0%, var(--color-border) 100%);
  border-radius: 12px;
  border: 2px solid var(--color-border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  ${props => props.$index === 1 && `
    top: 5px;
    left: 5px;
    transform: rotate(-3deg);
  `}

  ${props => props.$index === 2 && `
    top: 10px;
    left: 10px;
    transform: rotate(-6deg);
  `}
`;

const ProfileCardFront = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 120px;
  height: 160px;
  background: linear-gradient(135deg, rgba(var(--color-accent-rgb), 0.9) 0%, var(--color-accent-strong) 100%);
  border-radius: 12px;
  border: 2px solid var(--color-accent-strong);
  box-shadow: 0 8px 24px rgba(var(--color-accent-rgb), 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(2deg);

  :root:not([data-theme='light']) & {
    background: linear-gradient(135deg, var(--color-accent-strong) 0%, var(--color-accent-strong) 100%);
    opacity: 1;
    border: 2px solid rgba(255, 255, 255, 1);
  }
`;

const ProfileCardQuestion = styled.span`
  font-size: 56px;
  font-weight: bold;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const ProfileCardCostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProfileCardCostText = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #f39c12;
`;

const ProfileCardDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6c757d;
  text-align: center;
  line-height: 1.6;
`;

const PurchaseToastOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease-in-out;
  cursor: pointer;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PurchaseToastContent = styled.div`
  background: #ffffff !important;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: var(--modal-shadow-large);
  text-align: center;
  min-width: 280px;
  width: 100%;
  max-width: 320px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const PurchaseToastConfirmButton = styled.button`
  width: 100%;
  margin-top: 4px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: var(--color-accent-strong);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-sizing: border-box;

  &:hover {
    opacity: 0.9;
  }
  &:active {
    opacity: 0.85;
  }
`;

const PurchaseToastImage = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 12px;
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(var(--color-accent-rgb), 0.3);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PurchaseToastImageNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #343a40;
`;

const PurchaseToastMessageWrap = styled.div`
  border-radius: 12px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--color-block-border);
  display: inline-block;
`;

const PurchaseToastMessage = styled.div`
  font-size: 18px;
  color: #000000 !important;
  font-weight: 700;
`;

const PurchaseToastLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const PurchaseToastCostRow = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #f39c12;
`;

const InsufficientToast = styled.div`
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4757;
  color: white;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  z-index: 2000;
`;

const ProfileDrawResultOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
`;

const ProfileDrawResultModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 20px;
  padding: 32px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 2001;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
`;

const ProfileDrawTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const ProfileDrawImageContainer = styled.div`
  width: 180px;
  height: 220px;
  border-radius: 16px;
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-border);
`;

const ProfileDrawQuestion = styled.span`
  font-size: 64px;
  font-weight: 700;
  color: #aaa;
`;

const ProfileDrawResultImage = styled.div`
  width: 140px;
  height: 180px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(var(--color-accent-rgb), 0.9) 0%, var(--color-accent-strong) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileDrawResultText = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
`;

const ProfileDrawResultLabel = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const ProfileDrawCloseButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 10px;
  background: var(--color-accent-strong);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;
